import { ArrowLeft, Loader2, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BrandAsset, BrandKit } from "../models/BrandKit";
import { ExtractedArticleImage } from "../models/Post";
import { PexelsImage, PexelsService } from "../services/PexelsService";
import { getSignedUrl } from "../services/Firebase";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import { useToast } from "./ui/use-toast";

type LibraryTab = "stock" | "brandkit" | "article";

interface ImageLibraryPanelProps {
  onBack: () => void;
  onSelectPexelsImage: (image: PexelsImage) => void;
  onSelectArticleImage: (image: ExtractedArticleImage) => void;
  onSelectBrandKitImage: (asset: BrandAsset) => void;
  articleImages: ExtractedArticleImage[];
  brandKits: BrandKit[];
  defaultTab?: LibraryTab;
}

export function ImageLibraryPanel({
  onBack,
  onSelectPexelsImage,
  onSelectArticleImage,
  onSelectBrandKitImage,
  articleImages,
  brandKits,
  defaultTab,
}: ImageLibraryPanelProps) {
  const { toast } = useToast();

  // Determine available tabs and default
  const hasArticleImages = articleImages.length > 0;
  const kitsWithImages = useMemo(
    () =>
      brandKits
        .map((kit) => ({
          kit,
          images: (kit.brandAssets || []).filter(
            (a) => a.type === "image" && a.processingStatus !== "failed"
          ),
        }))
        .filter(({ images }) => images.length > 0),
    [brandKits]
  );
  const hasBrandKitImages = kitsWithImages.length > 0;

  const resolvedDefault =
    defaultTab && isTabAvailable(defaultTab) ? defaultTab : "stock";
  const [activeTab, setActiveTab] = useState<LibraryTab>(resolvedDefault);

  function isTabAvailable(tab: LibraryTab) {
    if (tab === "article") return hasArticleImages;
    if (tab === "brandkit") return hasBrandKitImages;
    return true;
  }

  // ── Pexels search state ──
  const [searchQuery, setSearchQuery] = useState("");
  const [pexelsImages, setPexelsImages] = useState<PexelsImage[]>([]);
  const [pexelsLoading, setPexelsLoading] = useState(false);
  const [pexelsPage, setPexelsPage] = useState(1);
  const [pexelsHasMore, setPexelsHasMore] = useState(true);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const performSearch = useCallback(
    async (query: string, pageNum: number = 1, append: boolean = false) => {
      if (!query.trim()) {
        setPexelsImages([]);
        return;
      }
      setPexelsLoading(true);
      try {
        const { auth } = await import("../services/Firebase");
        const user = auth.currentUser;
        if (!user) throw new Error("User not authenticated");
        const idToken = await user.getIdToken();

        const response = await PexelsService.searchImages(query, idToken, {
          page: pageNum,
          per_page: 20,
        });

        if (append) {
          setPexelsImages((prev) => [...prev, ...response.photos]);
        } else {
          setPexelsImages(response.photos);
        }
        setPexelsHasMore(
          response.photos.length > 0 &&
            response.photos.length === response.per_page
        );
      } catch (error: any) {
        console.error("Error searching images:", error);
        toast({
          title: "Search Failed",
          description: error.message || "Failed to search images",
          variant: "destructive",
        });
      } finally {
        setPexelsLoading(false);
      }
    },
    [toast]
  );

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (searchQuery.trim()) {
      searchTimeoutRef.current = setTimeout(() => {
        setPexelsPage(1);
        performSearch(searchQuery, 1, false);
      }, 300);
    } else {
      setPexelsImages([]);
    }
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchQuery, performSearch]);

  const handleLoadMore = () => {
    const nextPage = pexelsPage + 1;
    setPexelsPage(nextPage);
    performSearch(searchQuery, nextPage, true);
  };

  // ── Article / Brand kit URL resolution ──
  const [resolvedUrls, setResolvedUrls] = useState<Record<string, string>>({});
  const [loadingPaths, setLoadingPaths] = useState<Set<string>>(new Set());

  const resolveUrl = useCallback(
    async (path: string) => {
      if (resolvedUrls[path]) return resolvedUrls[path];
      setLoadingPaths((s) => new Set(s).add(path));
      try {
        const url = await getSignedUrl(path);
        setResolvedUrls((prev) => ({ ...prev, [path]: url }));
        return url;
      } catch {
        return "";
      } finally {
        setLoadingPaths((s) => {
          const next = new Set(s);
          next.delete(path);
          return next;
        });
      }
    },
    [resolvedUrls]
  );

  // Batch-resolve article image URLs when tab is first shown
  useEffect(() => {
    if (activeTab !== "article" || !hasArticleImages) return;
    articleImages.forEach((img) => {
      if (!resolvedUrls[img.storagePath]) resolveUrl(img.storagePath);
    });
  }, [activeTab, articleImages, hasArticleImages]);

  // Batch-resolve brand kit thumbnail URLs when tab is first shown
  useEffect(() => {
    if (activeTab !== "brandkit" || !hasBrandKitImages) return;
    kitsWithImages.forEach(({ images }) =>
      images.forEach((a) => {
        const path = a.thumbnailPath || a.storagePath;
        if (!resolvedUrls[path]) resolveUrl(path);
      })
    );
  }, [activeTab, kitsWithImages, hasBrandKitImages]);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center mb-3">
        <button
          onClick={onBack}
          className="mr-2 p-1 hover:bg-gray-100 rounded-md transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h3 className="text-lg font-medium">Image Library</h3>
      </div>

      {/* Tab bar */}
      <Tabs value={activeTab} onValueChange={(tab) => setActiveTab(tab as LibraryTab)}>
        <TabsList className="w-full">
          <TabsTrigger value="stock" className="flex-1 text-xs">
            Stock
          </TabsTrigger>
          {hasBrandKitImages && (
            <TabsTrigger value="brandkit" className="flex-1 text-xs">
              Brand Kit
            </TabsTrigger>
          )}
          {hasArticleImages && (
            <TabsTrigger value="article" className="flex-1 text-xs">
              Source
            </TabsTrigger>
          )}
        </TabsList>
      </Tabs>

      {/* Tab content — conditional render to avoid Radix layout issues */}
      <div className="flex-1 flex flex-col min-h-0 mt-3">
        {activeTab === "stock" && (
          <>
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search for images..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex-1 overflow-y-auto p-1">
              {pexelsLoading && pexelsImages.length === 0 ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : pexelsImages.length === 0 ? (
                <div className="text-center text-muted-foreground py-8 text-sm">
                  {searchQuery.trim()
                    ? "No images found"
                    : "Enter a search term to find images"}
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    {pexelsImages.map((image) => (
                      <div
                        key={image.id}
                        onClick={() => onSelectPexelsImage(image)}
                        className="relative aspect-square rounded-md overflow-hidden cursor-pointer hover:opacity-80 transition-all"
                      >
                        <img
                          src={image.src.medium}
                          alt={image.alt || "Pexels image"}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                  {pexelsHasMore && (
                    <div className="mt-4 flex justify-center">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleLoadMore}
                        disabled={pexelsLoading}
                      >
                        {pexelsLoading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Loading...
                          </>
                        ) : (
                          "Load More"
                        )}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}

        {activeTab === "brandkit" && (
          <div className="flex-1 overflow-y-auto p-1">
            <div className="space-y-4">
              {kitsWithImages.map(({ kit, images }) => (
                <div key={kit.id}>
                  {kitsWithImages.length > 1 && (
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                      {kit.name}
                    </p>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    {images.map((asset) => {
                      const thumbPath =
                        asset.thumbnailPath || asset.storagePath;
                      const url = resolvedUrls[thumbPath];
                      const isLoading = loadingPaths.has(thumbPath);

                      return (
                        <div
                          key={asset.id}
                          onClick={() =>
                            !isLoading && url && onSelectBrandKitImage(asset)
                          }
                          className={`relative rounded-md overflow-hidden cursor-pointer hover:opacity-80 transition-all ${isLoading || !url ? "cursor-not-allowed opacity-50" : ""}`}
                        >
                          <div className="aspect-video bg-black/5">
                            {isLoading ? (
                              <div className="w-full h-full flex items-center justify-center">
                                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                              </div>
                            ) : url ? (
                              <img
                                src={url}
                                alt={asset.description || "Brand asset"}
                                className="w-full h-full object-contain"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                                Failed to load
                              </div>
                            )}
                          </div>
                          <div className="p-2 text-left bg-muted/30">
                            <p className="text-xs text-muted-foreground truncate">
                              {asset.description || "Brand image"}
                            </p>
                            <p className="text-xs text-muted-foreground/60">
                              {asset.dimensions.width} ×{" "}
                              {asset.dimensions.height}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === "article" && (
          <div className="flex-1 overflow-y-auto p-1">
            <div className="grid grid-cols-2 gap-3">
              {articleImages.map((image, index) => {
                const url = resolvedUrls[image.storagePath];
                const isLoading = loadingPaths.has(image.storagePath);

                return (
                  <div
                    key={image.storagePath}
                    onClick={() =>
                      !isLoading && url && onSelectArticleImage(image)
                    }
                    className={`relative rounded-md overflow-hidden cursor-pointer hover:opacity-80 transition-all ${isLoading || !url ? "cursor-not-allowed opacity-50" : ""}`}
                  >
                    <div className="aspect-video bg-black/5">
                      {isLoading ? (
                        <div className="w-full h-full flex items-center justify-center">
                          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                      ) : url ? (
                        <img
                          src={url}
                          alt={image.altText || `Source image ${index + 1}`}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                          Failed to load
                        </div>
                      )}
                    </div>
                    <div className="p-2 text-left bg-muted/30">
                      <p className="text-xs text-muted-foreground truncate">
                        {image.altText || image.context || `Image ${index + 1}`}
                      </p>
                      <p className="text-xs text-muted-foreground/60">
                        {image.dimensions.width} × {image.dimensions.height}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
